import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  Settings,
  Calendar,
  FolderOpen,
  Plus,
  Zap,
  Building,
  BarChart3,
  DollarSign,
  Globe,
  ChevronLeft,
  ChevronDown,
  Menu,
  Sparkles,
  Castle,
  Ship,
} from "lucide-react";
import CreateTripModal from "../agent/trips/CreateTripModal";
import CreateClientModal from "../agent/clients/CreateClientModal";
import { useUser } from "../../context/UserContext";
import { useIsRouteAvailable } from "../../hooks/usePhaseAccess";
import { useNavMode } from "../../hooks/useNavMode";
import { isParkProAdmin, isAgentOrAgentAdmin } from "../../utils/roleUtils";
import "../../styles/modern-design-system.css";

/**
 * SidebarV2 - Navigation with Admin Mode Switch
 * 
 * Features:
 * - Agent Mode: Lean, task-focused navigation
 * - Admin Mode: Admin-only navigation (only if canAccessAdmin)
 * - Settings always pinned in footer
 * - Respects rollout phase gating (useIsRouteAvailable)
 * - Respects role gating (canAccessAdmin, isParkProAdmin)
 * - No "Coming Soon" items (hidden if not available)
 * 
 * NOTE: Routes not yet in navigation (documented for future):
 * - /agent/crm/pipeline
 * - /agent/crm/subscriptions
 * - /agent/admin/attention
 * - /agent/admin/activity-log (in AdminShell but not in Admin Mode here)
 * - /agent/admin/clients (in AdminShell but not in Admin Mode here)
 */
const SidebarV2 = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedTripManagement, setExpandedTripManagement] = useState(true);
  const [showCreateTripModal, setShowCreateTripModal] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const { user } = useUser();
  const { navMode, setNavMode, hasAdminAccess } = useNavMode();
  const location = useLocation();
  const navigate = useNavigate();
  const drawerRef = useRef(null);

  const showAllFourDestinations = isParkProAdmin(user?.role);
  const showTwoDestinationsWithDisneylandComingSoon = isAgentOrAgentAdmin(user?.role);

  // Auto-detect admin routes and switch to Admin Mode if needed
  useEffect(() => {
    const isOnAdminRoute = location.pathname.startsWith('/agent/admin');
    if (isOnAdminRoute && hasAdminAccess && navMode !== 'admin') {
      setNavMode('admin');
    }
  }, [location.pathname, hasAdminAccess, navMode, setNavMode]);

  // Derive Trip Management expanded: open when on a trip page, else use toggle state (no effect needed)
  const isOnTripPage =
    location.pathname.startsWith("/agent/crm/trips") ||
    location.pathname.startsWith("/agent/crm/disney-world") ||
    location.pathname.startsWith("/agent/crm/disneyland") ||
    location.pathname.startsWith("/agent/crm/universal-orlando") ||
    location.pathname.startsWith("/agent/crm/disney-cruise");
  const tripManagementExpanded = isOnTripPage || expandedTripManagement;

  // Phase-based access checks for Agent Mode (hooks must be called at top level)
  const isCrmTripsAvailable = useIsRouteAvailable('/agent/crm/trips');
  const isCrmClientsAvailable = useIsRouteAvailable('/agent/crm/clients');

  // Phase-based access checks for Admin Mode
  const isAdminDashboardAvailable = useIsRouteAvailable('/agent/admin/dashboard');
  const isAdminAgentsAvailable = useIsRouteAvailable('/agent/admin/agents');
  const isAdminFinancialAvailable = useIsRouteAvailable('/agent/admin/financial');
  const isAdminWhiteLabelingAvailable = useIsRouteAvailable('/agent/admin/white-labeling');
  const isAdminReportsAvailable = useIsRouteAvailable('/agent/admin/reports');

  const isActive = (path) => {
    if (path === '/agent/dashboard') {
      return location.pathname === '/agent/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Focus trap and ESC close for mobile drawer
  useEffect(() => {
    if (mobileOpen) {
      drawerRef.current?.focus();
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          setMobileOpen(false);
        }
        if (e.key === "Tab") {
          const focusableEls = drawerRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstEl = focusableEls[0];
          const lastEl = focusableEls[focusableEls.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [mobileOpen]);

  const handleClientCreated = (newClient) => {
    window.dispatchEvent(new CustomEvent('clientCreated', { detail: newClient }));
    if (!location.pathname.startsWith('/agent/crm/clients')) {
      navigate('/agent/crm/clients');
    }
  };

  // Render Agent Mode navigation
  const renderAgentMode = (onClick) => (
    <div className="flex flex-col h-full min-h-0 no-underline-anim-scope">
      {/* HEADER SECTION - Non-scrollable, pinned top */}
      <div className="flex-shrink-0 border-b border-gray-200">
        {/* Home Button */}
        <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4">
          <Link
            to="/agent/dashboard"
            className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/dashboard") ? "bg-gray-200 shadow-sm" : ""
            } sm:px-4 sm:py-2.5`}
            onClick={onClick}
          >
            <Home className="mr-2 w-4 h-4 sm:mr-3" />
            Dashboard
          </Link>
        </div>

        {/* Quick Actions - Only in Agent Mode */}
        <div className="px-3 pb-3 sm:px-4 sm:pb-4">
          <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-200 sm:p-3">
            <div className="flex items-center mb-1.5">
              <Zap className="mr-2 w-3.5 h-3.5 text-gray-600" />
              <span className="text-xs font-semibold tracking-wide text-gray-800 uppercase">
                Quick Actions
              </span>
            </div>
            <div className="space-y-0.5">
              <button
                onClick={() => {
                  setShowCreateTripModal(true);
                  onClick?.();
                }}
                className="flex items-center px-3 py-2 w-full text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200"
              >
                <Plus className="mr-2 w-4 h-4 sm:mr-3" />
                New Trip
              </button>
              <button
                onClick={() => {
                  setShowCreateClientModal(true);
                  onClick?.();
                }}
                className="flex items-center px-3 py-2 w-full text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200"
              >
                <Plus className="mr-2 w-4 h-4 sm:mr-3" />
                Add Client
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* NAV SECTION - Scrollable, takes remaining space */}
      <div className="overflow-y-auto flex-1 min-h-0">
        <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-3">
          <div className="px-3 mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase sm:px-4">
            Navigation
          </div>
          
          {/* Client Management */}
          {isCrmClientsAvailable && (
            <Link
              to="/agent/crm/clients"
              className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/clients") ? "bg-gray-200 shadow-sm" : ""
              } sm:px-4 sm:py-2.5`}
              onClick={onClick}
            >
              <Users className="mr-2 w-4 h-4 sm:mr-3" />
              Client Management
            </Link>
          )}

          {/* Trip Management - Collapsible dropdown (parkProAdmin: 4 destinations; agent/agentAdmin: Disney World + Disneyland Coming Soon) */}
          <div className="mb-2">
            <button
              type="button"
              className={`flex justify-between items-center px-3 py-2 w-full text-sm font-medium rounded-lg transition-all duration-200 text-black hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${
                isActive("/agent/crm/trips") ||
                isActive("/agent/crm/disney-world") ||
                isActive("/agent/crm/disneyland") ||
                isActive("/agent/crm/universal-orlando") ||
                isActive("/agent/crm/disney-cruise")
                  ? "bg-gray-200 shadow-sm"
                  : ""
              } sm:px-4 sm:py-2.5`}
              onClick={() => setExpandedTripManagement((prev) => !prev)}
            >
              <div className="flex items-center">
                <Calendar className="mr-2 w-4 h-4 sm:mr-3" />
                Trip Management
              </div>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  tripManagementExpanded ? "rotate-180" : ""}`}
              />
            </button>
            {tripManagementExpanded && (
              <div className="mt-1 ml-6 space-y-0.5">
                {isCrmTripsAvailable && (
                  <Link
                    to="/agent/crm/trips"
                    className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/trips") ? "bg-gray-200 shadow-sm" : ""}`}
                    onClick={onClick}
                  >
                    <BarChart3 className="mr-2 w-4 h-4 sm:mr-3" />
                    All Trips
                  </Link>
                )}
                {showAllFourDestinations && (
                  <>
                    <Link
                      to="/agent/crm/disney-world"
                      className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/disney-world") ? "bg-gray-200 shadow-sm" : ""}`}
                      onClick={onClick}
                    >
                      <Sparkles className="mr-2 w-4 h-4 sm:mr-3" />
                      Disney World
                    </Link>
                    <Link
                      to="/agent/crm/disneyland"
                      className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/disneyland") ? "bg-gray-200 shadow-sm" : ""}`}
                      onClick={onClick}
                    >
                      <Castle className="mr-2 w-4 h-4 sm:mr-3" />
                      Disneyland
                    </Link>
                    <Link
                      to="/agent/crm/universal-orlando"
                      className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/universal-orlando") ? "bg-gray-200 shadow-sm" : ""}`}
                      onClick={onClick}
                    >
                      <Globe className="mr-2 w-4 h-4 sm:mr-3" />
                      Universal Studios
                    </Link>
                    <Link
                      to="/agent/crm/disney-cruise"
                      className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/disney-cruise") ? "bg-gray-200 shadow-sm" : ""}`}
                      onClick={onClick}
                    >
                      <Ship className="mr-2 w-4 h-4 sm:mr-3" />
                      Disney Cruise
                    </Link>
                  </>
                )}
                {showTwoDestinationsWithDisneylandComingSoon && (
                  <>
                    <Link
                      to="/agent/crm/disney-world"
                      className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/crm/disney-world") ? "bg-gray-200 shadow-sm" : ""}`}
                      onClick={onClick}
                    >
                      <Sparkles className="mr-2 w-4 h-4 sm:mr-3" />
                      Disney World
                    </Link>
                    <div
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-400 bg-transparent rounded-lg cursor-not-allowed"
                      aria-disabled="true"
                    >
                      <Castle className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400 sm:mr-3" />
                      <span className="flex-1 text-gray-400">Disneyland</span>
                      <span className="flex-shrink-0 px-2 py-0.5 ml-2 text-xs font-medium text-white bg-gradient-to-r from-gray-600 to-gray-700 rounded-full whitespace-nowrap">
                        Coming Soon
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Wonder Vault */}
          <Link
            to="/agent/documents"
            className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-black hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${
              isActive("/agent/documents") ? "bg-gray-200 shadow-sm" : ""
            } sm:px-4 sm:py-2.5`}
            onClick={onClick}
          >
            <FolderOpen className="mr-2 w-4 h-4 sm:mr-3" />
            Wonder Vault
          </Link>
        </div>
      </div>

      {/* FOOTER SECTION - Non-scrollable, pinned bottom */}
      <div className="flex-shrink-0 mt-auto px-3 py-2.5 bg-white border-t border-gray-300 sm:px-4 sm:py-3">
        <Link
          to="/agent/settings"
          className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/settings") ? "bg-gray-200 shadow-sm" : ""
          } sm:px-4 sm:py-2.5`}
          onClick={onClick}
        >
          <Settings className="mr-2 w-4 h-4 sm:mr-3" />
          Settings
        </Link>
      </div>
    </div>
  );

  // Render Admin Mode navigation
  const renderAdminMode = (onClick) => (
    <div className="flex flex-col h-full min-h-0 no-underline-anim-scope">
      {/* HEADER SECTION - Non-scrollable, pinned top */}
      <div className="flex-shrink-0 border-b border-gray-200">
        {/* Dashboard */}
        {isAdminDashboardAvailable ? (
          <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4">
            <Link
              to="/agent/admin/dashboard"
              className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/admin/dashboard") ? "bg-gray-200 shadow-sm" : ""
              } sm:px-4 sm:py-2.5`}
              onClick={onClick}
            >
              <BarChart3 className="mr-2 w-4 h-4 sm:mr-3" />
              Agency Dashboard
            </Link>
          </div>
        ) : (
          // Empty header spacer to maintain layout when dashboard not available
          <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4" />
        )}
      </div>

      {/* NAV SECTION - Scrollable, takes remaining space */}
      <div className="overflow-y-auto flex-1 min-h-0">
        <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-3">
          <div className="px-3 mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase sm:px-4">
            Administration
          </div>

          {/* Agent Management */}
          {isAdminAgentsAvailable && (
            <Link
              to="/agent/admin/agents"
              className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/admin/agents") ? "bg-gray-200 shadow-sm" : ""
              } sm:px-4 sm:py-2.5`}
              onClick={onClick}
            >
              <Users className="mr-2 w-4 h-4 sm:mr-3" />
              Agent Management
            </Link>
          )}

          {/* Financial Management */}
          {isAdminFinancialAvailable && (
            <Link
              to="/agent/admin/financial"
              className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/admin/financial") ? "bg-gray-200 shadow-sm" : ""
              } sm:px-4 sm:py-2.5`}
              onClick={onClick}
            >
              <DollarSign className="mr-2 w-4 h-4 sm:mr-3" />
              Financial Management
            </Link>
          )}

          {/* White Labeling */}
          {isAdminWhiteLabelingAvailable && (
            <Link
              to="/agent/admin/white-labeling"
              className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/admin/white-labeling") ? "bg-gray-200 shadow-sm" : ""
              } sm:px-4 sm:py-2.5`}
              onClick={onClick}
            >
              <Globe className="mr-2 w-4 h-4 sm:mr-3" />
              White Labeling
            </Link>
          )}

          {/* Reports & Analytics */}
          {isAdminReportsAvailable && (
            <Link
              to="/agent/admin/reports"
              className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/admin/reports") ? "bg-gray-200 shadow-sm" : ""
              } sm:px-4 sm:py-2.5`}
              onClick={onClick}
            >
              <BarChart3 className="mr-2 w-4 h-4 sm:mr-3" />
              Reports & Analytics
            </Link>
          )}
        </div>
      </div>

      {/* FOOTER SECTION - Non-scrollable, pinned bottom */}
      <div className="flex-shrink-0 mt-auto px-3 py-2.5 bg-white border-t border-gray-300 sm:px-4 sm:py-3 space-y-1">
        <Link
          to="/agent/settings"
          className={`flex items-center px-3 py-2 text-sm font-medium text-black rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 ${ isActive("/agent/settings") ? "bg-gray-200 shadow-sm" : ""
          } sm:px-4 sm:py-2.5`}
          onClick={onClick}
        >
          <Settings className="mr-2 w-4 h-4 sm:mr-3" />
          Settings
        </Link>
        <button
          onClick={() => {
            // Navigate away from admin route first, then switch mode
            // This prevents auto-switch from immediately switching back
            navigate('/agent/dashboard');
            setNavMode('agent');
            onClick?.();
          }}
          className="flex items-center px-3 py-2 w-full text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 sm:px-4 sm:py-2.5"
        >
          <ChevronLeft className="mr-2 w-4 h-4 sm:mr-3" />
          Exit Admin Mode
        </button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        /* Disable underline/hover underline animations inside the sidebar */
        .no-underline-anim-scope a,
        .no-underline-anim-scope a:hover,
        .no-underline-anim-scope a:focus,
        .no-underline-anim-scope button,
        .no-underline-anim-scope button:hover,
        .no-underline-anim-scope button:focus {
          text-decoration: none !important;
          box-shadow: none !important;
        }
        .no-underline-anim-scope a::after,
        .no-underline-anim-scope a:hover::after,
        .no-underline-anim-scope a:focus::after,
        .no-underline-anim-scope button::after,
        .no-underline-anim-scope button:hover::after,
        .no-underline-anim-scope button:focus::after {
          content: none !important;
          display: none !important;
        }
        
        /* Universal cross-browser and cross-device scrolling */
        .no-underline-anim-scope {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          overflow: hidden !important;
          height: 100% !important;
          position: relative !important;
        }
        
        /* Ensure flex containers work properly - only nav section should scroll */
        .no-underline-anim-scope > div:first-child {
          flex-shrink: 0 !important;
        }
        .no-underline-anim-scope > div:last-child {
          flex-shrink: 0 !important;
        }
        
        /* Scrollbar styling for nav section (applied via overflow-y-auto class) */
        @media (min-width: 769px) {
          .no-underline-anim-scope > div[class*="overflow-y-auto"] {
            scrollbar-width: thin;
            scrollbar-color: #cbd5e0 transparent;
          }
          
          .no-underline-anim-scope > div[class*="overflow-y-auto"]::-webkit-scrollbar {
            width: 6px;
          }
          
          .no-underline-anim-scope > div[class*="overflow-y-auto"]::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .no-underline-anim-scope > div[class*="overflow-y-auto"]::-webkit-scrollbar-thumb {
            background-color: #cbd5e0;
            border-radius: 3px;
          }
          
          .no-underline-anim-scope > div[class*="overflow-y-auto"]::-webkit-scrollbar-thumb:hover {
            background-color: #a0aec0;
          }
        }
      `}</style>
      {/* Desktop Sidebar */}
      <aside
        className="hidden sticky top-0 z-40 flex-col w-64 h-full min-h-0 bg-white border-r border-gray-200 shadow-sm md:flex"
        aria-label="Sidebar navigation"
        style={{ overflow: 'hidden' }}
      >
        {navMode === 'admin' ? renderAdminMode() : renderAgentMode()}
      </aside>

      {/* Mobile Toggle */}
      <button
        className="fixed top-3 left-3 z-50 p-2 bg-white rounded-lg border border-gray-200 shadow-lg md:hidden"
        onClick={() => setMobileOpen(true)}
        aria-label="Open sidebar menu"
      >
        <Menu className="w-5 h-5 text-gray-600" />
      </button>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div
          className="flex fixed inset-0 z-50 backdrop-blur-sm bg-black/60"
          aria-modal="true"
          role="dialog"
          aria-label="Sidebar drawer"
        >
          <div
            ref={drawerRef}
            className="flex flex-col w-72 h-full min-h-0 bg-white shadow-2xl outline-none sm:w-80"
            tabIndex={-1}
          >
            {/* Mobile Header */}
            <div className="flex flex-shrink-0 justify-between items-center p-3 border-b border-gray-200 sm:p-4">
              <h3 className="text-lg font-semibold text-gray-900">Menu</h3>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close sidebar menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {navMode === 'admin' ? renderAdminMode(() => setMobileOpen(false)) : renderAgentMode(() => setMobileOpen(false))}
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateTripModal
        isOpen={showCreateTripModal}
        onClose={() => setShowCreateTripModal(false)}
        onTripCreated={() => {}}
      />

      <CreateClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        onClientCreated={handleClientCreated}
      />
    </>
  );
};

export default SidebarV2;
